<template>
  <article class="comment">
    <aside class="comment__profile-image">
      <img v-if="commenter.avatar" :src="commenter.avatar" alt="" />
      <span v-else class="avatar-placeholder demi">
        {{ displayName.charAt(0) }}
      </span>
    </aside>

    <div class="comment__main">
      <h3 class="comment__byline" data-hj-suppress>
        <img v-if="commenter.avatar" :src="commenter.avatar" alt="" class="sm-only" />
        <span v-else class="avatar-placeholder demi sm-only">
          {{ displayName.charAt(0) }}
        </span>
        <!-- <span>By</span> <a :href="`/community/profile/${ commenter.username }`">{{ commenter.displayName }}</a><span class="comment__time"><span class="dash"> — </span>{{ comment.timeAgo }}</span> -->
        <span>By</span> {{ displayName
        }}<span class="comment__time"><span class="dash"> — </span>{{ comment.timeAgo }}</span>
        <span v-if="role !== ''" class="user-badge admin baseline">{{ role }}</span>
      </h3>

      <comment-edit-form
        v-if="isEditing"
        :parent-id="parentId"
        :comment-id="comment.id"
        :body-orig="comment.comment"
        :image-url="comment.imageUrl"
        :image-data-string-orig="comment.imageDataString"
        @cancel-edit="cancelEdit"
      />

      <div v-show="image && !isEditing" class="comment__image">
        <img :src="image" />
      </div>

      <callout v-if="comment.imageError && !isEditing" type="info"
        ><div>Comment saved, but unable to process image</div></callout
      >

      <!-- eslint-disable vue/no-v-html -->
      <!-- eslint-disable-next-line -->
      <p
        v-for="commentParagraph in displayComment"
        v-show="!isEditing"
        v-html="fbLinkify(decode(commentParagraph))"
      ></p>

      <div v-if="comment.isEdited" class="comment__edited">Edited</div>

      <footer class="comment__footer">
        <button
          v-if="isLoggedIn"
          class="discussion-btn reply"
          @click.prevent="focusForm(commentFormId)"
        >
          <span class="iconfont-return" aria-hidden="true" /> Reply
        </button>

        <button v-else class="discussion-btn reply" @click.prevent="showLoginModal">
          <span class="iconfont-return" aria-hidden="true" /> Reply
        </button>

        <button
          class="discussion-btn thumbs-up"
          :disabled="makingApiCall"
          @click.prevent="toggleLike()"
        >
          <span :class="likeClass" aria-hidden="true" /> {{ likeCountText }}
        </button>

        <div v-if="showMoreOptions" :class="['more-options', { 'multiple-options': isOwner }]">
          <button v-if="isOwner" class="btn -link" @click="openEditCommentForm()">
            Edit Comment
          </button>
          <button v-if="isOwner" class="btn -link" @click="openDeleteConfirmationModal(comment.id)">
            Remove Comment
          </button>
          <button v-if="!isOwner" class="btn -link" @click="openFlagModal(comment.id)">
            Report Spam or Abuse
          </button>
        </div>

        <button
          v-if="isLoggedIn"
          class="btn-more comment__more"
          @click="toggleCommentOptions(comment.id)"
        >
          <i class="f" />
          <i class="m" />
          <i class="l" />
        </button>
      </footer>
    </div>

    <div v-if="hasChildren || addingComment" class="comment__children">
      <comment
        v-for="childComment in commentChildren"
        :key="childComment.id"
        :comment="childComment"
        :level="1"
        :parent-id="comment.id"
      />

      <comment-loader v-if="addingComment || loadingChildren" id="comment-loader" />

      <button
        v-if="!loading && showMoreRepliesBtn"
        class="btn -block -alt"
        @click="
          loadMoreChildComments({
            offset: comment.children.length,
            parentId: comment.id,
          })
        "
      >
        Load More Replies
      </button>
    </div>

    <comment-form v-if="form.active && form.id === comment.id" :id="comment.id" />
  </article>
</template>

<script>
// @flow
import commentForm from './comment-form.vue'
import commentLoader from './comment-loader.vue'
import commentEditForm from './comment-edit-form.vue'
import Callout from '../notifications/callout.vue'
import { mapState, mapActions, mapMutations } from 'vuex'
import { CHILD_LOAD_AMOUNT } from 'app/constants'
import { fbAPIGet, fbAPIPost } from 'app/utils/http-api'
import { buildComment } from 'app/utils/comments'

export default {
  name: 'Comment',
  components: { commentForm, commentLoader, commentEditForm, Callout },

  /* eslint-disable vue/require-default-prop */
  props: {
    // todo: we are mutating prop when editing
    comment: Object,
    level: {
      default: 0,
      type: Number,
    },
    parentId: {
      default: -1,
      type: Number,
    },
  },
  data() {
    return {
      // when editing store out the original comment text
      // todo: new way to handle rollbacks
      origComment: '',
      isEditing: false,
      canLoadMore: true,
      makingApiCall: false,
      decoder: null,
      // get FB urls.  Not 100% that this is best regex for urls, but FB urls shouldn't be too fancy
      // https://stackoverflow.com/questions/1500260/detect-urls-in-text-with-javascript
      // eslint-disable-next-line
      fbUrlRegex:
        /(\bhttps:\/\/www\.fitnessblender\.com\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/gi, // eslint-disable-line
    }
  },
  computed: {
    isOwner() {
      return this.commenter.id === this.user.id
    },
    addingComment() {
      return this.loading && this.loadingParent === this.comment.id && this.loadingAmount === 1
    },
    displayName() {
      // if user is null (which it shouldn't be, but technically it could via API)
      // set displayName to Unknown.  This also handles the Avatar image/first letter
      return this.commenter.displayName || 'Unknown'
    },
    image() {
      return this.comment.imageDataString || this.comment.imageUrl
    },
    role() {
      let role = ''

      switch (this.commenter.role_id) {
        case 4:
          role = 'Admin'
          break
        default:
          role = ''
      }

      return role
    },
    displayComment() {
      return this.comment.comment.split(/\r?\n/g)
    },
    hasChildren() {
      const children = this.comment.children
      const userAdded = this.comment.userAdded
      const hasChildren = (children && children.length > 0) || (userAdded && userAdded.length > 0)

      return this.level === 0 && hasChildren
    },
    likeClass() {
      return 'iconfont' + (this.comment.isLiked ? '-like-select' : '-like')
    },
    likeCountText() {
      const likes = this.comment.likeCount
      if (this.comment.isLiked) {
        // return likes > 1 ? `You and ${likes} Others ` : 'You Like'
        return likes > 1 ? `${likes} Like` : `${likes} Like`
      } else {
        return likes > 0 ? `${likes} Like` : 'Like'
      }
    },
    loadingChildren() {
      return this.loading && this.loadingParent === this.comment.id && this.loadingAmount > 1
    },
    showMoreRepliesBtn() {
      const children = this.comment.children
      // Check that this comment has any children
      const hasChildren = this.hasChildren
      // Check that the response contains at least 5 otherwise there aren't any more.
      const canLoadMore = this.canLoadMore
      // Check that the length of the children minus the initial 2 is divisible by 5

      // TODO: clean up Laravel.commentId usage... I did the below as a quickfix
      // we load 50 replies if user is looking at a single comment, otherwise
      // the default is 2 replies
      const defaultLoadedChildren = Laravel.commentId ? 50 : 2
      const isDivisible = (children.length - defaultLoadedChildren) % CHILD_LOAD_AMOUNT === 0

      return hasChildren && canLoadMore && isDivisible
    },
    showMoreOptions() {
      return this.commentOptionsId === this.comment.id
    },
    commenter() {
      return this.comment.user
    },
    commentFormId() {
      return this.level === 0 ? this.comment.id : this.parentId
    },
    commentChildren() {
      const children = this.comment.children
      let userAddedChildren = this.comment.userAdded

      if (userAddedChildren.length > 0) {
        const childrenIds = children.map((item) => {
          return item.id
        })

        userAddedChildren = userAddedChildren.filter((item) => {
          return childrenIds.indexOf(item.id) === -1
        })
      }

      return children.concat(userAddedChildren)
    },
    ...mapState({
      /* eslint-disable */
      commentOptionsId: (state: State) => state.comments.commentOptions.id,
      form: (state: State) => state.comments.commentForm,
      loading: (state: State) => state.comments.commentsLoading.status,
      loadingAmount: (state: State) => state.comments.commentsLoading.amount,
      loadingParent: (state: State) => state.comments.commentsLoading.parent,
      /* eslint-enable */
      isLoggedIn: (state: State) => state.user.isLoggedIn,
      user: (state: State) => state.user,
    }),
  },
  methods: {
    openEditCommentForm() {
      this.isEditing = true
      // todo: not needed with new style
      this.origComment = this.comment.comment
      // close options
      this.toggleCommentOptions()
    },
    cancelEdit() {
      //      this.comment.comment = this.origComment
      this.isEditing = false
    },
    fbLinkify(text) {
      return text.replace(this.fbUrlRegex, (url) => '<a href="' + url + '">' + url + '</a>')
    },
    // @see https://gomakethings.com/preventing-cross-site-scripting-attacks-when-using-innerhtml-in-vanilla-javascript/
    // Use this before adding our custom html (fb links)
    decode(html: string): string {
      this.decoder = this.decoder || document.createElement('div')
      this.decoder.textContent = html
      return this.decoder.innerHTML

      // old way, still had xss issues
      // Default way that Vue handles {{ }} decoding
      // https://github.com/vuejs/vue/issues/322
      // this.decoder.innerHTML = html
      // console.log(html, this.decoder.textContent);
      // return this.decoder.textContent
    },

    loadMoreChildComments(opts) {
      const { sort = 'newest', parentId, offset } = opts
      const url = `comments/child/${parentId}?sort=${sort}&offset=${offset}`

      this.form.active && this.closeCommentForm()

      this.setCommentsLoading({ amount: CHILD_LOAD_AMOUNT, parent: parentId })

      fbAPIGet(
        url,
        (res) => {
          const data = res.body.data
          if (!data || data.length === 0) {
            this.canLoadMore = false
          } else {
            this.addComments(data.map(buildComment))

            // if at least CHILD_LOAD_AMOUNT returned back, assume more comments exists
            if (res.ok && data && data.length === CHILD_LOAD_AMOUNT) {
              // BUT if the comment returned are already contained in our child comment list
              // we know we are at the end (do not show more CTA)
              const firstId = data[0] && data[0].id ? data[0].id : null
              const commentExists = this.commentChildren.includes(
                (comment) => comment.id === firstId,
              )

              if (commentExists) {
                this.canLoadMore = false
              } else {
                this.canLoadMore = true
              }
            }
          }
          this.hideLoading()
        },
        (err) => {
          this.hideLoading()
        },
      )
    },

    // similar function in discussion-footer.vue
    toggleLike() {
      if (!this.isLoggedIn) {
        this.showLoginModal()
        return
      }

      const opts = { id: this.comment.id, isLiked: this.comment.isLiked }
      this.setCommentLike(opts)

      this.makingApiCall = true

      const formData = new window.FormData()
      formData.append('likeable_type', 'comment')
      formData.append('likeable_id', this.comment.id)

      fbAPIPost(
        `like/${opts.isLiked ? 'remove' : 'add'}`,
        formData,
        (res) => {
          this.makingApiCall = false
        },
        (err) => {
          this.makingApiCall = false
          // undo like
          opts.isLiked = !opts.isLiked
          this.setCommentLike(opts)
        },
      )
    },
    ...mapActions([
      'focusForm',
      'openFlagModal',
      'openDeleteConfirmationModal',
      'showLoginModal',
      'setCommentLike',
      'hideLoading',
    ]),
    ...mapMutations([
      'toggleCommentOptions',
      'closeCommentForm',
      'setCommentsLoading',
      'addComments',
    ]),
  },
}
</script>
